import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Search, Star } from 'lucide-react';
import { useState, useSyncExternalStore } from 'react';
import { COMING_SOON } from '@/app/navigation';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ManagementReports } from '@/features/accountant/management-reports';
import { useDeleteCustomisation, useCustomisations } from '@/features/reports/api';
import {
  CATEGORIES,
  REPORTS,
  isReportAvailable,
  type ReportSpec,
} from '@/features/reports/catalogue';
import { favourites } from '@/features/reports/favourites';
import { SaveCustomisationDialog, VISIBILITY_LABELS } from '@/features/reports/save-dialog';
import type { Customisation } from '@/features/reports/types';
import { useMe } from '@/lib/clients';
import { formatDate } from '@/lib/format';
import { useOrgLocale } from '@/lib/org-locale';
import { cn } from '@/lib/utils';

type Tab = 'standard' | 'custom' | 'management';

export const Route = createFileRoute('/reports/')({
  component: ReportsHub,
  validateSearch: (s: Record<string, unknown>): { tab?: Tab } =>
    s['tab'] === 'custom' || s['tab'] === 'management' ? { tab: s['tab'] } : {},
});

/** Screen R1 (document 06): Standard reports by category with favourites, Custom reports and Management reports (A10). */
function ReportsHub() {
  const { tab = 'standard' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [q, setQ] = useState('');
  const custom = useCustomisations();
  const tabClass = (on: boolean) =>
    cn(
      '-mb-px border-b-2 pb-2 text-sm',
      on
        ? 'border-primary font-medium'
        : 'border-transparent text-muted-foreground hover:text-foreground',
    );
  return (
    <>
      <PageHeader title="Reports" />
      <div className="mb-4 flex items-center gap-6 border-b" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'standard'}
          className={tabClass(tab === 'standard')}
          onClick={() => void navigate({ search: {} })}
        >
          Standard
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'custom'}
          className={tabClass(tab === 'custom')}
          onClick={() => void navigate({ search: { tab: 'custom' } })}
        >
          Custom reports{custom.data?.length ? ` (${custom.data.length})` : ''}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'management'}
          className={tabClass(tab === 'management')}
          onClick={() => void navigate({ search: { tab: 'management' } })}
        >
          Management reports
        </button>
      </div>
      <div className="relative mb-4 w-[360px]">
        <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
        <Input
          className="bg-card pl-8"
          placeholder="Find report by name"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          aria-label="Find report by name"
        />
      </div>
      {tab === 'management' ? (
        <ManagementReports needle={q.trim().toLowerCase()} />
      ) : tab === 'custom' ? (
        <CustomReports needle={q.trim().toLowerCase()} />
      ) : (
        <StandardReports needle={q.trim().toLowerCase()} />
      )}
    </>
  );
}

function useFavourites(): Set<string> {
  return useSyncExternalStore(favourites.subscribe, favourites.get, favourites.get);
}

function StandardReports({ needle }: { needle: string }) {
  const fav = useFavourites();
  const { taxPack } = useOrgLocale();
  const match = (r: ReportSpec) =>
    (!r.pack || r.pack === taxPack) && (!needle || r.name.toLowerCase().includes(needle));
  const groups: Array<{ name: string; reports: ReportSpec[] }> = [
    {
      name: 'Favourites',
      reports: REPORTS.filter((r) => fav.has(r.key) && isReportAvailable(r.key)),
    },
    ...CATEGORIES.map((c) => ({ name: c, reports: REPORTS.filter((r) => r.category === c) })),
  ];
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {groups.map((g) => {
        const reports = g.reports.filter(match);
        if (reports.length === 0) return null;
        return (
          <section
            key={g.name}
            className={cn(
              'rounded-lg border bg-card p-4',
              g.name === 'Favourites' && 'md:col-span-2',
            )}
          >
            <h2 className="mb-2 text-base font-semibold">{g.name}</h2>
            <ul className="divide-y">
              {reports.map((r) => {
                const available = isReportAvailable(r.key);
                return (
                  <li key={r.key} className="flex items-center justify-between gap-3 py-2 text-sm">
                    {available && r.to ? (
                      <Link
                        to={r.to}
                        className="font-medium text-primary hover:underline"
                        title={r.description}
                      >
                        {r.name}
                      </Link>
                    ) : available ? (
                      <Link
                        to="/reports/$reportKey"
                        params={{ reportKey: r.key }}
                        className="font-medium text-primary hover:underline"
                        title={r.description}
                      >
                        {r.name}
                      </Link>
                    ) : (
                      <span
                        className="cursor-not-allowed text-muted-foreground opacity-60"
                        aria-disabled
                        title={COMING_SOON}
                        data-unavailable
                      >
                        {r.name}
                      </span>
                    )}
                    {available ? (
                      <button
                        type="button"
                        onClick={() => favourites.toggle(r.key)}
                        aria-pressed={fav.has(r.key)}
                        aria-label={
                          fav.has(r.key)
                            ? `Remove ${r.name} from favourites`
                            : `Add ${r.name} to favourites`
                        }
                        className="text-muted-foreground hover:text-primary"
                      >
                        <Star
                          className={cn('size-4', fav.has(r.key) && 'fill-primary text-primary')}
                        />
                      </button>
                    ) : (
                      <span className="text-xs text-muted-foreground">{COMING_SOON}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function CustomReports({ needle }: { needle: string }) {
  const custom = useCustomisations();
  const me = useMe();
  const remove = useDeleteCustomisation();
  const [editing, setEditing] = useState<Customisation | null>(null);
  const [deleting, setDeleting] = useState<Customisation | null>(null);
  if (custom.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (custom.isError)
    return <EmptyState title="Could not load custom reports" hint={custom.error.message} />;
  const rows = custom.data.filter(
    (c) => !needle || `${c.name} ${c.reportTitle}`.toLowerCase().includes(needle),
  );
  if (custom.data.length === 0)
    return (
      <EmptyState
        title="No custom reports yet"
        hint="Run a report, change it with Customise, then choose Save customisation."
        action={
          <Button asChild>
            <Link to="/reports">Standard reports</Link>
          </Button>
        }
      />
    );
  return (
    <>
      <div className="overflow-hidden rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 font-medium">Name</th>
              <th className="px-4 py-2 font-medium">Report</th>
              <th className="px-4 py-2 font-medium">Shared with</th>
              <th className="px-4 py-2 font-medium">Created by</th>
              <th className="px-4 py-2 font-medium">Last modified</th>
              <th className="px-4 py-2 text-right font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b last:border-0">
                <td className="px-4 py-2">
                  <Link
                    to="/reports/$reportKey"
                    params={{ reportKey: c.reportKey }}
                    search={{ ...c.settings, saved: c.id }}
                    className="font-medium text-primary hover:underline"
                  >
                    {c.name}
                  </Link>
                </td>
                <td className="px-4 py-2">{c.reportTitle}</td>
                <td className="px-4 py-2">{VISIBILITY_LABELS[c.visibility]}</td>
                <td className="px-4 py-2">{c.createdByName ?? ''}</td>
                <td className="px-4 py-2">{formatDate(c.updatedAt)}</td>
                <td className="px-4 py-2 text-right">
                  {c.mine ? (
                    <span className="inline-flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => setEditing(c)}>
                        Edit
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleting(c)}>
                        Delete
                      </Button>
                    </span>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground">
            No custom report matches.
          </p>
        ) : null}
      </div>
      <SaveCustomisationDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        reportKey={editing?.reportKey ?? ''}
        defaultName={editing?.name ?? ''}
        {...(editing ? { existing: editing } : {})}
        isStaff={me.data?.isStaff ?? false}
      />
      <Dialog open={deleting !== null} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete custom report?</DialogTitle>
            <DialogDescription>
              “{deleting?.name}” is removed for everyone it is shared with. The report itself and
              the books are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() =>
                deleting &&
                remove.mutate(deleting.id, {
                  onSuccess: () => {
                    toast(`Custom report "${deleting.name}" deleted`);
                    setDeleting(null);
                  },
                  onError: (e) => toast(e.message, { tone: 'error' }),
                })
              }
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
